/**
 * Permission types for role-based access control
 */

import { useAuth } from "@/context/AuthContext";

// Role levels matching backend
export const ROLE_LEVELS = {
  EXTERNAL: 1,
  CONTRIBUTOR: 2,
  PROJECT_MANAGER: 3,
  ADMIN: 4,
} as const;

// Role names mapping to levels
export const ROLE_NAMES_TO_LEVELS = {
  External: ROLE_LEVELS.EXTERNAL,
  Contributor: ROLE_LEVELS.CONTRIBUTOR,
  "Project Manager": ROLE_LEVELS.PROJECT_MANAGER,
  Admin: ROLE_LEVELS.ADMIN,
} as const;

export type RoleLevel = (typeof ROLE_LEVELS)[keyof typeof ROLE_LEVELS];

// Action permissions structure
export interface ResourceActions {
  [action: string]: boolean;
}

export interface ActionPermissions {
  users: ResourceActions;
  projects: ResourceActions;
  chip_pos_files: ResourceActions;
  masks: ResourceActions;
  test_labels: ResourceActions;
  structures: ResourceActions;
  spec_lists: ResourceActions;
  master_spec_lists: ResourceActions;
  measurement_requests: ResourceActions;
  measurement_diagnostics: ResourceActions;
}

// User permissions from API
export interface UserPermissions {
  pages: string[];
  actions: ActionPermissions;
  role: string;
  project_permissions: any; // For future PBAC implementation
}

// API response for permissions
export interface PermissionsResponse {
  permissions: UserPermissions;
}

// Local permission checking utilities
export interface PermissionConfig {
  // Resource-action to minimum level mapping
  actions: {
    [resource: string]: {
      [action: string]: RoleLevel;
    };
  };
  // Page to minimum level mapping
  pages: {
    [page: string]: RoleLevel;
  };
}

// Default permission configuration matching backend
export const DEFAULT_PERMISSION_CONFIG: PermissionConfig = {
  actions: {
    users: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.ADMIN,
      update: ROLE_LEVELS.ADMIN,
      delete: ROLE_LEVELS.ADMIN,
    },
    projects: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.PROJECT_MANAGER,
      update: ROLE_LEVELS.PROJECT_MANAGER,
      delete: ROLE_LEVELS.PROJECT_MANAGER,
    },
    chip_pos_files: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.CONTRIBUTOR,
      update: ROLE_LEVELS.CONTRIBUTOR,
      delete: ROLE_LEVELS.PROJECT_MANAGER,
    },
    masks: {
      readAll: ROLE_LEVELS.PROJECT_MANAGER,
      create: ROLE_LEVELS.ADMIN,
      update: ROLE_LEVELS.PROJECT_MANAGER,
      delete: ROLE_LEVELS.ADMIN,
      export: ROLE_LEVELS.ADMIN,
    },
    test_labels: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.PROJECT_MANAGER,
      update: ROLE_LEVELS.PROJECT_MANAGER,
      delete: ROLE_LEVELS.ADMIN,
    },
    spec_lists: {
      readAll: ROLE_LEVELS.EXTERNAL,
      create: ROLE_LEVELS.PROJECT_MANAGER,
      update: ROLE_LEVELS.PROJECT_MANAGER,
      delete: ROLE_LEVELS.ADMIN,
    },
    structures: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.PROJECT_MANAGER,
      update: ROLE_LEVELS.PROJECT_MANAGER,
      delete: ROLE_LEVELS.ADMIN,
    },
    master_spec_lists: {
      readAll: ROLE_LEVELS.ADMIN,
      create: ROLE_LEVELS.ADMIN,
      update: ROLE_LEVELS.ADMIN,
      delete: ROLE_LEVELS.ADMIN,
    },
    measurement_systems: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.ADMIN,
      update: ROLE_LEVELS.ADMIN,
      delete: ROLE_LEVELS.ADMIN,
    },
    measurement_requests: {
      readAll: ROLE_LEVELS.CONTRIBUTOR,
      create: ROLE_LEVELS.PROJECT_MANAGER,
      update: ROLE_LEVELS.PROJECT_MANAGER,
      delete: ROLE_LEVELS.ADMIN,
    },
    measurement_diagnostics: {
      read: ROLE_LEVELS.ADMIN,
    },
  },
  pages: {
    masks: ROLE_LEVELS.EXTERNAL,
    "measurement-requests": ROLE_LEVELS.EXTERNAL,
    "spec-lists": ROLE_LEVELS.EXTERNAL,
    "master-specs": ROLE_LEVELS.ADMIN,
    "request-planning": ROLE_LEVELS.CONTRIBUTOR,
    projects: ROLE_LEVELS.CONTRIBUTOR,
    users: ROLE_LEVELS.ADMIN,
    "test-labels": ROLE_LEVELS.CONTRIBUTOR,
    systems: ROLE_LEVELS.CONTRIBUTOR,
  },
};

export const useIsAdmin = () => {
  const { currentUser } = useAuth();

  return ["Admin"].includes(currentUser.role);
};
